import useAmplitude from '@eversports/amplitude-react/useAmplitude'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { BlockingError } from '../lib/custom-errors'
import getPhoenixUrlParams from '../lib/get-phoenix-url-params'

import {
  useUseCartCreationHandlerCreateCartFromCourtBookableItemMutation,
  useUseCartCreationHandlerCreateCartFromEventBookableItemMutation,
  useUseCartCreationHandlerCreateCartFromProductMutation,
  useUseCartCreationHandlerCreateCartFromVoucherMutation,
  useUseCartCreationHandlerTrackCheckoutStartMutation,
} from '../graphql'
import sessionUtils from './session-utils'

interface Data {
  createdCartId?: string
}

const useCartCreationHandler = (): Data => {
  const location = useLocation()
  const [isCheckoutStartTracked, setIsCheckoutStartTracked] = useState(false)
  const { deviceId, sessionId } = useAmplitude()

  const { origin, bookableItemId, productId, venueId, courtId, sportId, start, end, amount, imageId, message } =
    getPhoenixUrlParams(location.search)

  const [trackCheckoutStart] = useUseCartCreationHandlerTrackCheckoutStartMutation()

  const [createCartFromEventBookableItem, { data: eventBookableItemCart, error: eventBookableItemCartError }] =
    useUseCartCreationHandlerCreateCartFromEventBookableItemMutation({
      variables: { bookableItemId: bookableItemId as string, origin },
    })

  const [createCartFromCourtBookableItem, { data: courtBookableItemCart, error: courtBookableItemCartError }] =
    useUseCartCreationHandlerCreateCartFromCourtBookableItemMutation({
      variables: {
        courtId: courtId as string,
        sportId: sportId as string,
        start: start as string,
        end: end as string,
        origin,
      },
    })

  const [createCartFromProduct, { data: productCart, error: productCartError }] =
    useUseCartCreationHandlerCreateCartFromProductMutation({
      variables: { productId: productId as string, venueId: venueId as string, origin },
    })

  const [createCartFromVoucher, { data: voucherCart, error: voucherCartError }] =
    useUseCartCreationHandlerCreateCartFromVoucherMutation({
      variables: {
        amount: amount as number,
        imageId: imageId as string,
        message: message as string,
        venueId: venueId as string,
        origin,
      },
    })

  useEffect(() => {
    sessionUtils.removeStepper()
    sessionUtils.removeConfirmationWasSuccessful()
    sessionUtils.setFamilyAccountReturnUrl(
      `/auth/family-account-login?redirectPath=${encodeURIComponent(
        window.location.pathname + window.location.search,
      )}`,
    )

    if (bookableItemId) {
      // eslint-disable-next-line  @typescript-eslint/no-floating-promises
      createCartFromEventBookableItem()
      return
    }

    if (courtId && sportId && start && end) {
      // eslint-disable-next-line  @typescript-eslint/no-floating-promises
      createCartFromCourtBookableItem()
      return
    }

    if (productId && venueId) {
      // eslint-disable-next-line  @typescript-eslint/no-floating-promises
      createCartFromProduct()
      return
    }

    if (amount && venueId && imageId) {
      // eslint-disable-next-line  @typescript-eslint/no-floating-promises
      createCartFromVoucher()
      return
    }

    // No valid url params combination means a cart can't be created
    throw new Error(`Invalid url: ${location.pathname}${location.search}`)
  }, [])

  if (eventBookableItemCartError || courtBookableItemCartError || productCartError || voucherCartError) {
    const error = eventBookableItemCartError || courtBookableItemCartError || productCartError || voucherCartError
    throw new Error(`Error creating Cart: ${error!.message}`)
  }

  if (eventBookableItemCart || courtBookableItemCart || productCart || voucherCart) {
    const eventBookableItemCartExpectedError =
      eventBookableItemCart &&
      eventBookableItemCart.createCartFromEventBookableItem.__typename === 'ExpectedErrors' &&
      eventBookableItemCart.createCartFromEventBookableItem.errors[0]

    const courtBookableItemCartExpectedError =
      courtBookableItemCart &&
      courtBookableItemCart.createCartFromCourtBookableItem.__typename === 'ExpectedErrors' &&
      courtBookableItemCart.createCartFromCourtBookableItem.errors[0]

    const productCartExpectedError =
      productCart &&
      productCart.createCartFromProduct.__typename === 'ExpectedErrors' &&
      productCart.createCartFromProduct.errors[0]

    const voucherCartExpectedError =
      voucherCart &&
      voucherCart.createCartFromVoucher.__typename === 'ExpectedErrors' &&
      voucherCart.createCartFromVoucher.errors[0]

    const expectedError =
      eventBookableItemCartExpectedError ||
      courtBookableItemCartExpectedError ||
      productCartExpectedError ||
      voucherCartExpectedError

    // In this case we just want to treat the expected error the same as a normal one
    // Due to always being a breaking error for the cart creation
    if (expectedError) {
      throw new BlockingError('There was a problem creating the cart', expectedError.message)
    }

    const eventBookableItemCartId =
      eventBookableItemCart &&
      eventBookableItemCart.createCartFromEventBookableItem.__typename === 'Cart' &&
      eventBookableItemCart.createCartFromEventBookableItem.id

    const courtBookableItemCartId =
      courtBookableItemCart &&
      courtBookableItemCart.createCartFromCourtBookableItem.__typename === 'Cart' &&
      courtBookableItemCart.createCartFromCourtBookableItem.id

    const productCartId =
      productCart && productCart.createCartFromProduct.__typename === 'Cart' && productCart.createCartFromProduct.id

    const voucherCartId =
      voucherCart && voucherCart.createCartFromVoucher.__typename === 'Cart' && voucherCart.createCartFromVoucher.id

    const createdCartId = (eventBookableItemCartId ||
      courtBookableItemCartId ||
      productCartId ||
      voucherCartId) as string

    if (deviceId && sessionId && !isCheckoutStartTracked) {
      trackCheckoutStart({
        variables: { cartId: createdCartId, deviceId, sessionId: Number(sessionId) },
      })
      setIsCheckoutStartTracked(true)
    }

    return { createdCartId }
  }

  return { createdCartId: undefined }
}

export default useCartCreationHandler
